import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader, SectionCard, EmptyState } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ClipboardList, Plus, FileDown } from "lucide-react";

export default function WawiInventur() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const write = can(user?.role, "wawi:write");
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const [start, setStart] = useState({ warehouse_id: "", category: "" });

  const { data: whs } = useQuery({ queryKey: ["wawi-warehouses"], queryFn: async () => (await api.get("/wawi/warehouses")).data });
  const { data: cats } = useQuery({ queryKey: ["wawi-categories"], queryFn: async () => (await api.get("/wawi/categories")).data });
  const { data, refetch } = useQuery({ queryKey: ["wawi-counts"], queryFn: async () => (await api.get("/wawi/counts")).data });
  const warehouses = whs?.items || [];

  const begin = async () => {
    if (!start.warehouse_id) return toast.error("Bitte Lager wählen");
    try {
      const r = await api.post("/wawi/counts", { warehouse_id: start.warehouse_id, category: start.category === "all" ? "" : start.category });
      toast.success("Inventur gestartet"); setOpen(false); refetch(); openCount(r.data.id);
    } catch (err) { toast.error(apiError(err)); }
  };
  const openCount = async (id) => {
    const r = await api.get(`/wawi/counts/${id}`); setSel(r.data.count);
  };
  const setLine = (i, v) => setSel((s) => { const l = [...s.lines]; l[i] = { ...l[i], counted: v === "" ? null : Number(v) }; return { ...s, lines: l }; });
  const saveDraft = async () => {
    try { await api.put(`/wawi/counts/${sel.id}`, { lines: sel.lines }); toast.success("Zwischengespeichert"); }
    catch (err) { toast.error(apiError(err)); }
  };
  const book = async () => {
    try {
      await api.put(`/wawi/counts/${sel.id}`, { lines: sel.lines });
      const r = await api.post(`/wawi/counts/${sel.id}/book`);
      toast.success(`Inventur gebucht · ${r.data.corrections} Korrekturen`); setSel(null); refetch();
      qc.invalidateQueries({ queryKey: ["wawi-stock"] });
    } catch (err) { toast.error(apiError(err)); }
  };
  const downloadProtocol = async (id) => {
    try {
      const r = await api.get(`/wawi/counts/${id}/protocol.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `inventur-${id.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiError(err)); }
  };

  if (sel) {
    return (
      <div>
        <PageHeader title="Inventur erfassen" description={`Soll-Ist-Vergleich · ${sel.lines.length} Positionen`}
          actions={<div className="flex gap-2">
            {sel.status === "abgeschlossen" && <Button variant="outline" className="gap-1.5" onClick={() => downloadProtocol(sel.id)} data-testid="count-pdf-detail"><FileDown className="h-4 w-4" />Protokoll (PDF)</Button>}
            <Button variant="outline" onClick={() => setSel(null)}>Zurück</Button>
          </div>} />
        <SectionCard title="Zählliste">
          {sel.lines.length === 0 ? <EmptyState title="Kein Bestand in diesem Lager" /> : (
            <div className="space-y-2">
              {sel.lines.map((ln, i) => {
                const diff = ln.counted == null ? null : ln.counted - ln.expected;
                return (
                  <div key={i} className="flex items-center justify-between gap-3 rounded-md border p-3" data-testid={`count-line-${i}`}>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{ln.article_name}{ln.variant_name ? ` · ${ln.variant_name}` : ""}</div>
                      <div className="text-xs text-muted-foreground">Soll: {ln.expected}{diff != null && diff !== 0 && <span className={diff > 0 ? "text-emerald-600 ml-2" : "text-red-600 ml-2"}>Δ {diff > 0 ? "+" : ""}{diff}</span>}</div>
                    </div>
                    {sel.status !== "abgeschlossen" && write ? (
                      <Input type="number" min="0" className="w-24" placeholder="Ist" value={ln.counted ?? ""} onChange={(e) => setLine(i, e.target.value)} data-testid={`count-input-${i}`} />
                    ) : <Badge variant="outline">Ist: {ln.counted ?? "—"}</Badge>}
                  </div>
                );
              })}
            </div>
          )}
          {sel.status !== "abgeschlossen" && write && sel.lines.length > 0 && (
            <div className="mt-4 flex gap-2">
              <Button variant="outline" onClick={saveDraft} data-testid="count-save-draft">Zwischenspeichern</Button>
              <Button onClick={book} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="count-book">Korrekturen buchen</Button>
            </div>
          )}
        </SectionCard>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Inventur" description="Bestandszählung je Lager/Kategorie mit Soll-Ist-Vergleich"
        actions={write && <Button className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => setOpen(true)} data-testid="new-count-button"><Plus className="h-4 w-4" />Neue Inventur</Button>} />
      <SectionCard title={`${(data?.items || []).length} Inventuren`}>
        {(data?.items || []).length === 0 ? <EmptyState icon={ClipboardList} title="Keine Inventuren" /> : (
          <div className="space-y-2">
            {data.items.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-3 rounded-md border p-3 hover:bg-muted/50" data-testid={`count-row-${c.id}`}>
                <button onClick={() => openCount(c.id)} className="min-w-0 flex-1 text-left">
                  <div className="text-sm font-medium">{c.warehouse_name}{c.category ? ` · ${c.category}` : ""}</div>
                  <div className="text-xs text-muted-foreground">{c.line_count} Positionen · {new Date(c.created_at).toLocaleDateString("de-DE")}</div>
                </button>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge className={c.status === "abgeschlossen" ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "bg-amber-500/10 text-amber-700 dark:text-amber-300"}>{c.status === "abgeschlossen" ? `Gebucht (${c.corrections ?? 0})` : "Offen"}</Badge>
                  {c.status === "abgeschlossen" && <Button size="sm" variant="outline" onClick={() => downloadProtocol(c.id)} data-testid={`count-pdf-${c.id}`}><FileDown className="h-4 w-4" /></Button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Neue Inventur</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Lager</Label>
              <Select value={start.warehouse_id} onValueChange={(v) => setStart({ ...start, warehouse_id: v })}>
                <SelectTrigger data-testid="count-warehouse"><SelectValue placeholder="Lager wählen" /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Kategorie (optional)</Label>
              <Select value={start.category || "all"} onValueChange={(v) => setStart({ ...start, category: v })}>
                <SelectTrigger data-testid="count-category"><SelectValue placeholder="Alle" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Alle Kategorien</SelectItem>{(cats?.items || []).map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button onClick={begin} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="count-start">Starten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
